import type { InputHTMLAttributes } from 'react'
import { formatPhone } from '../utils/formatters'

type PhoneInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> & {
  /** Current display value, e.g. "(415) 594-0795" (or "+4479…" international). */
  value: string
  /** Called with the masked value on every change (typing and paste alike). */
  onValueChange: (value: string) => void
}

/**
 * Phone input with the US (XXX) XXX-XXXX mask applied as the user types.
 *
 * Every change runs through `formatPhone`: letters and stray punctuation are
 * stripped, a pasted "+1…"/"1…" country code is unwrapped instead of shifting
 * the mask, and a non-US "+…" paste is kept as +digits. Validate the value
 * with `isValidPhone` (utils/validators.ts) — backed by the same E.164
 * normalizer the SMS send path uses. Shared by the manual eligibility form and
 * the doctor/clinic forms. All other input props (id, className, placeholder,
 * onBlur, disabled, …) pass straight through.
 */
export default function PhoneInput({ value, onValueChange, ...rest }: PhoneInputProps) {
  return (
    <input
      {...rest}
      type="tel"
      value={value}
      onChange={e => onValueChange(formatPhone(e.target.value))}
    />
  )
}
